import { BaseEditViewStore, BaasicDropdownStore } from "core/stores";
import { action, computed, observable, runInAction, set } from "mobx";
import _ from "lodash";
import { NikoPatientTemplateClinicalEditForm } from "application/niko-patient/forms";
import { getErrorMessage } from "core/utils";
import { LookupService } from "common/services";
import { HistoryService } from "../../history/services";
import NikoDxCodeDropdownStore from "./NikoDxCodeDropdownStore";
class NikoPatientTemplateClinicalEditViewStore extends BaseEditViewStore {
  @observable accessor productStore;
  @observable accessor clinicalStatusStore;
  @observable accessor missingDocumentStore;
  @observable accessor doctorStore;
  @observable accessor dxCode1Store;
  @observable accessor dxCode2Store;
  @observable accessor dxCode3Store;
  @observable accessor dxCode4Store;
  @observable accessor dxCode5Store;
  @observable accessor dxCode6Store;
  @observable accessor dxCode7Store;
  @observable accessor dxCode8Store;
  @observable accessor disabledItem;
  @observable accessor patientTemplate;
  @observable accessor isSyncInProgress = false;
  @observable accessor missingDocsPreviousValue;
  @observable accessor mappedMissingDocuments = [];

  @computed
  get isPsoEditDisabled() {
    // ISG-2532: Edit mode removed from the PSO page - clinical fields are now always editable.
    // Original edit-mode gating kept (commented) in case edit mode needs to be restored.
    // return Boolean(this.isPso) && this.patientTemplate.isEditDisabled;
    return false;
  }

  constructor(rootStore, componentsProps) {
    const patientTemplate = componentsProps && componentsProps.patientTemplate;
    const pageViewStore = componentsProps && componentsProps.pageViewStore.pageViewStore;
    super(rootStore, {
      name: "niko-patient-template-clinical-edit",
      Form: NikoPatientTemplateClinicalEditForm,
      id: patientTemplate.id,
      autoInit: false,
      actions: () => {
        return {
          get: async () => {
            const missingDocs = patientTemplate.patientTemplateMissingDocuments.map((i) => ({
              ...i.missingDocument,
              missingDocument: i.id,
              note: i.note,
              isPreviousDos: i.isPreviousDos,
              missingDocumentId: i.missingDocumentId
            }));
            return {
              id: patientTemplate.id,
              clinicalStatusId: patientTemplate.clinicalStatusId,
              salesOrderNote: patientTemplate.salesOrderNote,
              expirationDate: patientTemplate.expirationDate,
              orderingDoctorId: patientTemplate.orderingDoctorId,
              patientTemplateMissingDocuments: missingDocs,
              patientTemplateMissingDocumentIds: missingDocs,
              clinicalStatusNote: patientTemplate.clinicalStatusNote,
              history: { userName: '', eventDate: null, fieldName: '' }
            };
          },
          update: async (payload) => {
            await pageViewStore.patientTemplateService.update(payload);
          },
          updatePatientDetails: async (resource) => {
            return await pageViewStore.nikoPatientDetailService.updatePatientInformation(resource);
          },
          updateClinicalStatus: async (resource) => {
            await pageViewStore.patientTemplateService.updateClinicalStatus(resource);
          }
        };
      },
      FormClass: NikoPatientTemplateClinicalEditForm,
    });

    this.rootStore = rootStore;
    this.pageViewStore = pageViewStore;
    this.patientTemplate = patientTemplate;
    set(this.patientTemplate, 'history', { userName: '', eventDate: null, fieldName: '' });

    this.lookupService = new LookupService(rootStore.application.baasic.apiClient);
    this.templatesViewStore = componentsProps && componentsProps.pageViewStore;
    this.isPso = componentsProps && componentsProps.isPso;
    // ISG-2622: capability flag for the post-Niko-Sales-Order "swap" clinical-status flow. Passed only by the PSO
    // page (which provides openChangeClinicalStatusModal); other pages using this shared component (e.g. Patient
    // Management) leave it falsy and keep the plain editable dropdown.
    this.canChangeClinicalStatus = componentsProps && componentsProps.canChangeClinicalStatus;
    this.isMasterPatient = Boolean(componentsProps && componentsProps.isMasterPatient);
    this.historyService = new HistoryService(rootStore.application.baasic.apiClient);

    this.clinicalStatusStore = new BaasicDropdownStore({},
      {
        fetchFunc: async (term = "") => {
          const res = await this.pageViewStore.clinicalStatusService.find({ rpp: 50, isMasterPatient: this.isMasterPatient, searchQuery: term || undefined });
          return (res && res.data && res.data.item) || [];
        },
        onChange: async () => {
          await this.updatePatientTemplate("clinicalStatusId", true);
        },
      }
    );

    this.missingDocumentStore = this.patientTemplate.missingDocumentStore;

    this.doctorStore = new BaasicDropdownStore(
      { textField: "orderingDoctor", filterable: true, returnObject: true, },
      {
        fetchFunc: async (term) => {
          const res = await this.pageViewStore.localDoctorService.find({ searchQuery: term, rpp: 20, isNiko: true });
          const items = (res && res.data && res.data.item) || [];
          return _.map(items, (d) => ({ ...d, orderingDoctor: `${d.name} - ${d.npi}${d.primaryAddressState ? ` - ${d.primaryAddressState}` : ""}` }));
        },
        onChange: async () => {
          await this.updatePatientTemplate("orderingDoctorId", true);
        },
      }
    );

    const createDxCodeDropdownStore = (fieldName) => new NikoDxCodeDropdownStore(
      {
        filterable: true,
        dataItemKey: "id",
        textField: "code",
        returnObject: true
      },
      {
        fetchFunc: async (term) => {
          if (!term || term.trim() === "") {
            term = undefined;
          }

          const res = await this.pageViewStore.diagnosisCodeService.find({
            searchQuery: term,
            itemCategoryId: this.patientTemplate.itemCategoryId,
            isBillable: true,
            rpp: 10
          });

          return res.data.item;
        },
        onChange: async () => {
          await this.updatePatientTemplate(fieldName, true);
        }
      }
    );

    this.dxCode1Store = createDxCodeDropdownStore("dxCode1", 0);
    this.dxCode2Store = createDxCodeDropdownStore("dxCode2", 1);
    this.dxCode3Store = createDxCodeDropdownStore("dxCode3", 2);
    this.dxCode4Store = createDxCodeDropdownStore("dxCode4", 3);
    this.dxCode5Store = createDxCodeDropdownStore("dxCode5", 4);
    this.dxCode6Store = createDxCodeDropdownStore("dxCode6", 5);
    this.dxCode7Store = createDxCodeDropdownStore("dxCode7", 6);
    this.dxCode8Store = createDxCodeDropdownStore("dxCode8", 7);

    this.fetch([this.initialize()]);
  }

  @action
  async initialize() {
    if (this.pageViewStore && this.pageViewStore.clinicalLoadStart) this.pageViewStore.clinicalLoadStart();
    try {
      if (this.isEdit) {
        await this.getResource(this.id);
        await this.setDefaultStoreValue();
        await this.setInitialDropdownValues();
        this.mappedMissingDocuments = this.patientTemplate.patientTemplateMissingDocuments
          ? this.patientTemplate.patientTemplateMissingDocuments.map(i => ({
            ...i,
            name: i.missingDocument.name,
            note: i.note,
            isPreviousDos: i.isPreviousDos,
            patientTemplateId: this.patientTemplate.id
          }))
          : [];
        this.missingDocsPreviousValue = await this.getMissingDocsPreviousValue();
        if (!this.patientTemplate.clinicalStatusId) {
          this.clinicalStatusStore.setValue({ id: null, name: "Select" });
        }
      }
      else {
        this.form.clear();
      }
    } finally {
      if (this.pageViewStore && this.pageViewStore.clinicalLoadEnd) this.pageViewStore.clinicalLoadEnd();
    }
  }

  @action.bound
  updatePatientData(propertyName, value) {
    if (value < 0.001 && value !== null) {
      value = 0;
    }
    this.pageViewStore.nikoPatientDetail[propertyName] = value;
  }

  @action.bound
  async setInitialDropdownValues() {
    if (this.patientTemplate.initialPatientTemplate) {
      if (this.patientTemplate.initialPatientTemplate.OrderingDoctorId) {
        const doctor = (await this.pageViewStore.localDoctorService.get(this.patientTemplate.initialPatientTemplate.OrderingDoctorId)).data;
        set(this.patientTemplate.initialPatientTemplate, 'DoctorName', doctor.name);
      }
      if (this.patientTemplate.initialPatientTemplate.ClinicalStatusId) {
        const clinicalStatus = (await this.pageViewStore.clinicalStatusService.get(this.patientTemplate.initialPatientTemplate.ClinicalStatusId)).data;
        set(this.patientTemplate.initialPatientTemplate, 'ClinicalStatusName', clinicalStatus.name);
      }
      if (this.patientTemplate.initialPatientTemplate.PatientTemplateDiagnosisCodes) {
        await this.patientTemplate.initialPatientTemplate.PatientTemplateDiagnosisCodes.forEach(async element => {
          element.DiagnosisCode = (await this.pageViewStore.diagnosisCodeService.get(element.DiagnosisCodeId)).data;
        });
      }
    }
  }

  @action.bound
  async updatePatient() {
    if (!this.actions.updatePatientDetails) return;
    this.disabledItem = true;
    this.form.setFieldsDisabled(true);
    try {
      if (this.pageViewStore.nikoPatientDetail.tempHeight != this.pageViewStore.nikoPatientDetail.height || this.pageViewStore.nikoPatientDetail.tempWeight != this.pageViewStore.nikoPatientDetail.weight) {

        const zeroToNull = value => value === 0 ? null : value;
        this.pageViewStore.nikoPatientDetail.tempHeight = zeroToNull(this.pageViewStore.nikoPatientDetail.tempHeight);
        this.pageViewStore.nikoPatientDetail.tempWeight = zeroToNull(this.pageViewStore.nikoPatientDetail.tempWeight);

        await this.actions.updatePatientDetails({ id: this.pageViewStore.nikoPatientDetail.id, height: this.pageViewStore.nikoPatientDetail.tempHeight, weight: this.pageViewStore.nikoPatientDetail.tempWeight });
        this.pageViewStore.nikoPatientDetail.height = this.pageViewStore.nikoPatientDetail.tempHeight;
        this.pageViewStore.nikoPatientDetail.weight = this.pageViewStore.nikoPatientDetail.tempWeight;
      }
      this.disabledItem = false;
      this.form.setFieldsDisabled(false);
    }
    catch (err) {
      this.disabledItem = false;
      this.form.setFieldsDisabled(false);
      this.rootStore.notificationStore.error(getErrorMessage(err, 'Error occurred while trying to update Patient information.'));
      this.pageViewStore.nikoPatientDetail.tempHeight = this.pageViewStore.nikoPatientDetail.height;
      this.pageViewStore.nikoPatientDetail.tempWeight = this.pageViewStore.nikoPatientDetail.weight;
    }
  }

  @action.bound
  async setDefaultStoreValue() {
    if (this.patientTemplate.patientTemplateDiagnosisCodes && this.patientTemplate.patientTemplateDiagnosisCodes.length > 0) {
      const sortedDxCodes = _.sortBy(this.patientTemplate.patientTemplateDiagnosisCodes, "order");

      sortedDxCodes.forEach((dxCodeItem) => {
        const order = dxCodeItem.order;
        const diagnosisCode = dxCodeItem.diagnosisCode;

        if (order === 1 && this.dxCode1Store) {
          this.dxCode1Store.setValue(diagnosisCode);
          this.form.$("dxCode1").value = diagnosisCode.id;
        }
        else if (order === 2 && this.dxCode2Store) {
          this.dxCode2Store.setValue(diagnosisCode);
          this.form.$("dxCode2").value = diagnosisCode.id;
        }
        else if (order === 3 && this.dxCode3Store) {
          this.dxCode3Store.setValue(diagnosisCode);
          this.form.$("dxCode3").value = diagnosisCode.id;
        }
        else if (order === 4 && this.dxCode4Store) {
          this.dxCode4Store.setValue(diagnosisCode);
          this.form.$("dxCode4").value = diagnosisCode.id;
        }
        else if (order === 5 && this.dxCode5Store) {
          this.dxCode5Store.setValue(diagnosisCode);
          this.form.$("dxCode5").value = diagnosisCode.id;
        }
        else if (order === 6 && this.dxCode6Store) {
          this.dxCode6Store.setValue(diagnosisCode);
          this.form.$("dxCode6").value = diagnosisCode.id;
        }
        else if (order === 7 && this.dxCode7Store) {
          this.dxCode7Store.setValue(diagnosisCode);
          this.form.$("dxCode7").value = diagnosisCode.id;
        }
        else if (order === 8 && this.dxCode8Store) {
          this.dxCode8Store.setValue(diagnosisCode);
          this.form.$("dxCode8").value = diagnosisCode.id;
        }
      });
    }

    if (this.patientTemplate.clinicalStatusId && this.patientTemplate.clinicalStatus) {
      this.clinicalStatusStore.setValue(this.patientTemplate.clinicalStatus);
    }

    if (this.patientTemplate.patientTemplateMissingDocuments && this.patientTemplate.patientTemplateMissingDocuments.length > 0) {
      const mappedDocs = this.patientTemplate.patientTemplateMissingDocuments.map((item) => ({
        ...item.missingDocument,
        missingDocumentId: item.id,
        note: item.note ? item.note : item.missingDocument.note,
      }));
      this.missingDocumentStore.setValue(mappedDocs);
    }

    if (this.patientTemplate.orderingDoctorId && this.patientTemplate.orderingDoctor) {
      this.doctorStore.setValue({
        ...this.patientTemplate.orderingDoctor,
        orderingDoctor: `${this.patientTemplate.orderingDoctor.name} - ${this.patientTemplate.orderingDoctor.npi}${this.patientTemplate.orderingDoctor.primaryAddressState ? ` - ${this.patientTemplate.orderingDoctor.primaryAddressState}` : ""}`,
      });
    }
  }

  @action.bound
  async updatePatientTemplate(propertyName, submit = false) {
    const currentValue = this.patientTemplate[propertyName];
    const formValue = this.form.$(propertyName).value;
    if (submit || currentValue !== formValue) {
      this.form.submit();
    }
  }

  @action.bound
  updateClinicalStatusNote(newNote) {
    this.patientTemplate.clinicalStatusNote = newNote;
  }

  @action.bound
  async updateResource(resource) {
    if (!this.actions.update) return;

    this.disabledItem = true;
    this.form.setFieldsDisabled(true);

    const dxVals = [resource.dxCode1, resource.dxCode2, resource.dxCode3, resource.dxCode4, resource.dxCode5, resource.dxCode6, resource.dxCode7, resource.dxCode8];
    const existingDxCodes = (this.patientTemplate.patientTemplateDiagnosisCodes || []).slice();

    const existingCodesMap = new Map();
    existingDxCodes.forEach((dx) => {
      existingCodesMap.set(dx.order, dx);
    });
    dxVals.forEach((dxVal, index) => {
      const order = index + 1;
      if (dxVal) {
        const existing = existingCodesMap.get(order);

        if (existing && existing.diagnosisCodeId === dxVal) {
          existingCodesMap.set(order, existing);
        }
        else {
          existingCodesMap.set(order, {
            diagnosisCodeId: dxVal,
            order: order,
          });
        }
      }
      else {
        existingCodesMap.delete(order)
      }
    });

    const mergedDxCodes = Array.from(existingCodesMap.values());

    const currentStatusId = this.form.$("clinicalStatusId").value;
    if (this.patientTemplate.clinicalStatusId !== currentStatusId) {
      const statusItem = this.clinicalStatusStore.originalItems.find(x => x.id === currentStatusId);
      this.updateClinicalStatusNote(statusItem ? statusItem.defaultNote : '');
    }

    const upsert = {
      nikoPatientDetailId: this.patientTemplate.nikoPatientDetailId,
      itemCategoryId: this.patientTemplate.itemCategoryId,
      subCategoryId: this.patientTemplate.subCategoryId,
      orderingDoctorId: resource.orderingDoctorId,
      expirationDate: resource.expirationDate,
      salesOrderNote: resource.salesOrderNote,
      patientTemplateMissingDocuments: this.mappedMissingDocuments.map(i => ({
        id: i.id,
        missingDocumentId: i.missingDocumentId || (i.missingDocument && i.missingDocument.id),
        note: i.note,
        isPreviousDos: i.isPreviousDos
      })),
      patientTemplateDiagnosisCodes: mergedDxCodes,
      patientTemplateItems: [],
      clinicalStatusNote: this.patientTemplate.clinicalStatusNote,
      clinicalStatusId: this.patientTemplate.clinicalStatusId,
      isUserDns: this.patientTemplate.isUserDns,
      note: this.patientTemplate.note
    };

    try {
      await this.actions.update({
        id: this.id,
        ...upsert,
      });

      await this.getTemplate();
      const updatedTemplate = (await this.pageViewStore.patientTemplateService.get(this.id, { embed: 'PatientTemplateDiagnosisCodes,PatientTemplateMissingDocuments,PatientTemplateItems,ClinicalStatus,SubCategory,ItemCategory,OrderingDoctor' })).data;

      // Keep the locally-enriched initialPatientTemplate snapshot (resolved DoctorName/ClinicalStatusName/dx codes)
      // instead of clobbering it with the fresh server copy, which has none of those resolved names.
      const { initialPatientTemplate, ...updatedTemplateRest } = updatedTemplate;
      Object.assign(this.patientTemplate, updatedTemplateRest);
      set(this.item, 'salesOrderNote', this.patientTemplate.salesOrderNote);
      set(this.item, 'expirationDate', this.patientTemplate.expirationDate);
      set(this.item, 'clinicalStatusId', this.patientTemplate.clinicalStatusId);
      set(this.item, 'orderingDoctorId', this.patientTemplate.orderingDoctorId);
      this.mappedMissingDocuments = this.patientTemplate.patientTemplateMissingDocuments
        ? this.patientTemplate.patientTemplateMissingDocuments.map(i => ({
          ...i,
          name: i.missingDocument.name,
          note: i.note,
          isPreviousDos: i.isPreviousDos,
          patientTemplateId: this.patientTemplate.id
        }))
        : [];
      this.missingDocsPreviousValue = await this.getMissingDocsPreviousValue();
    }
    catch (err) {
      return this.onUpdateError(err);
    }
    finally {
      this.disabledItem = false;
      this.form.setFieldsDisabled(false);
    }
  }

  @action.bound
  async updateClinicalStatus(clinicalStatus) {
    if (!this.actions.updateClinicalStatus) return;
    this.disabledItem = true;
    this.form.setFieldsDisabled(true);

    // Optimistically reflect the newly selected status so the dropdown + header badge show the
    // new name immediately (before any await, so it always runs).
    set(this.patientTemplate, 'clinicalStatus', clinicalStatus);
    set(this.patientTemplate, 'clinicalStatusId', clinicalStatus.id);
    set(this.item, 'clinicalStatusId', clinicalStatus.id);

    try {
      await this.actions.updateClinicalStatus({ id: this.id, clinicalStatusId: clinicalStatus.id });
      await this.getTemplate();
    }
    catch (err) {
      return this.onUpdateError(err);
    }
    finally {
      this.disabledItem = false;
      this.form.setFieldsDisabled(false);
    }
  }

  @action.bound
  async getTemplate() {
    const template = await this.pageViewStore.patientTemplateService.get(this.id, { embed: 'ClinicalStatus,SubCategory,PatientTemplateDiagnosisCodes,OrderingDoctor,PatientTemplateMissingDocuments,ItemCategory,PatientTemplateItems' });
    runInAction(() => {
      // Keep the locally-enriched initialPatientTemplate snapshot (resolved ClinicalStatusName/DoctorName/dx codes)
      // instead of clobbering it with the fresh server copy, which has none of those resolved names.
      const { initialPatientTemplate, ...rest } = template.data;
      Object.assign(this.patientTemplate, rest);
    });
  }

  @action.bound
  updateMissingDocNote(targetId, newNote, isPreviousDos) {
    this.mappedMissingDocuments = this.mappedMissingDocuments.map(doc =>
      doc.id === targetId ? { ...doc, note: newNote, isPreviousDos: isPreviousDos } : doc
    );
  }

  @action.bound
  async updateMissingDocuments(value) {
    this.mappedMissingDocuments = value.map(item => {
      if (item.abrv) {
        return {
          id: undefined,
          name: item.name,
          missingDocumentId: item.id,
          missingDocument: item,
          note: item.note,
          isPreviousDos: false,
          patientTemplateId: this.patientTemplate.id
        };
      }
      return item;
    });
    await this.updatePatientTemplate("patientTemplateMissingDocumentIds", true);
  }

  @action.bound
  openNikoTemplateMissingDocumentNoteModal(item) {
    this.pageViewStore.nikoTemplateMissingDocumentNoteModal.open({
      patientTemplateMissingDocument: item,
      setNote: !item.id,
      updateMissingDocNote: (targetId, newNote, isPreviousDos) => this.updateMissingDocNote(targetId, newNote, isPreviousDos)
    });
  }

  @action.bound
  openPatientTemplateClinicalStatusNoteModal() {
    this.pageViewStore.patientTemplateClinicalStatusNoteModal.open({
      patientTemplate: this.patientTemplate
    });
  }

  @action.bound
  async generateMedicalNecessityFormAndStandardWrittenOrder() {
    await this.generateMedicalNecessityForm();
    await this.generateStandardWrittenOrder();
  }

  @action.bound
  async generateMedicalNecessityForm() {
    try {
      if (this.patientTemplate.itemCategory.abrv === "enteral" && this.getMissingInformationText()) {
        this.pageViewStore.templatePNMissingInformationModal.open({
          missingInformation: this.getMissingInformationText(),
          generateMedicalNecessityForm: () => this.openMedicalNecessityWindow()
        });
      }
      else if (this.patientTemplate.itemCategory.abrv === "urological" && this.getMissingInformationUrological()) {
        this.pageViewStore.templatePNMissingInformationModal.open({
          missingInformation: ["Qty per day (system cannot determine)"],
          generateMedicalNecessityForm: () => this.openMedicalNecessityWindow()
        });
      }
      else {
        this.openMedicalNecessityWindow();
      }
    }
    catch (err) {
      this.rootStore.notificationStore.error("Error occurred while trying to generate Medical Necessity Form.");
    }
  }

  openMedicalNecessityWindow() {
    var url = `${this.pageViewStore.patientTemplateService.getMedicalNecessityFormReportUrl(this.patientTemplate.id)}?contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
    window.open(url, "_blank")
    this.rootStore.notificationStore.success("Progress Notes document successfully generated.");
  }

  getMissingInformationText() {
    let isPatientWeightMissing = this.pageViewStore.nikoPatientDetail && !this.pageViewStore.nikoPatientDetail.weight;
    let isPatientHeightMissing = this.pageViewStore.nikoPatientDetail && !this.pageViewStore.nikoPatientDetail.height;
    let isCalorieAmountMissing = this.patientTemplate.patientTemplateItems.some(x => x.product && x.product.itemTypeId && !x.itemCals);
    let isPatientGenderMissing = this.pageViewStore.nikoPatientDetail && !this.pageViewStore.nikoPatientDetail.nikoPatient.gender;
    let isMlphMissing = this.patientTemplate.patientTemplateItems.some(x => x.product && x.product.itemTypeId && !x.mlph);

    let missingItems = [];
    if (isPatientWeightMissing) {
      missingItems.push("weight");
    }
    if (isPatientHeightMissing) {
      missingItems.push("height");
    }
    if (isCalorieAmountMissing) {
      missingItems.push("total calories");
    }
    if (isPatientGenderMissing) {
      missingItems.push("gender");
    }
    if (isMlphMissing) {
      missingItems.push("flow rate");
    }
    missingItems.sort();

    if (missingItems.length > 0)
      return missingItems;
    return null;
  }

  getMissingInformationUrological() {
    return this.patientTemplate.patientTemplateItems.some(x =>
      (
        (x.product && x.product.procedureCode_ && (x.product.procedureCode_.abrv === "A4352")) ||
        (x.product && x.product.procedureCode_ && (x.product.procedureCode_.abrv === "A4353")) ||
        (x.product && x.product.procedureCode_ && (x.product.procedureCode_.abrv === "A4351"))
      ) && ((!x.quantity) || (x.quantity == 0) || (x.quantity % 28 != 0))
    );
  }

  @action.bound
  async generateStandardWrittenOrder() {
    try {
      var url = `${this.pageViewStore.patientTemplateService.getStandardWrittenOrderReportUrl(this.patientTemplate.id)}?contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
      window.open(url, "_blank")
      this.rootStore.notificationStore.success("Standard Written Order successfully generated.");
    }
    catch (err) {
      this.rootStore.notificationStore.error("Error occurred while trying to generate Standard Written Order.");
    }
  }

  @action.bound
  async syncOrderingDoctors() {
    if (this.isSyncInProgress)
      return;

    this.isSyncInProgress = true;
    try {
      await this.pageViewStore.localDoctorService.nikoSync();
      this.rootStore.notificationStore.success("Sync Niko Ordering Doctors successfully done.");
    } catch (err) {
      this.rootStore.notificationStore.error(err && err.data && err.data.errors ? err.data.errors : 'Error occurred while trying to sync niko ordering doctors.');
    }
    this.isSyncInProgress = false;
  }

  @action.bound
  async getFieldHistory(item, fieldName, value) {
    if (item.history) {
      const history = (await this.historyService.getRecordHistory({ recordId: item.id, fieldName: fieldName, value: value })).data;
      item.history.userName = history.userName;
      item.history.eventDate = history.eventDate;
      item.history.fieldName = fieldName;
    }
  }

  @action.bound
  async getMissingDocsPreviousValue() {
    // ISG-3006: templates without InitialValues (e.g. auto-created master patient templates) have no baseline to compare against.
    if (!this.patientTemplate.initialPatientTemplate) {
      return null;
    }

    if (!this.patientTemplate.initialPatientTemplate.PatientTemplateMissingDocuments || !this.patientTemplate.initialPatientTemplate.PatientTemplateMissingDocuments.some(x => x)) {
      return (!this.patientTemplate.patientTemplateMissingDocuments || !this.patientTemplate.patientTemplateMissingDocuments.some(x => x)) ? null : "";
    }

    await this.patientTemplate.initialPatientTemplate.PatientTemplateMissingDocuments.forEach(element => {
      element.MissingDocument = this.missingDocumentStore.originalItems.find(x => x.id === element.MissingDocumentId);
    });

    let missingDocuments = _.sortBy(this.patientTemplate.patientTemplateMissingDocuments, md => md.missingDocument.name);
    let missingDocumentInitials = _.sortBy(this.patientTemplate.initialPatientTemplate.PatientTemplateMissingDocuments, mdi => mdi.MissingDocument && mdi.MissingDocument.name);

    if (missingDocuments.map(mdi => mdi.missingDocument.name).join(", ") != missingDocumentInitials.map(mdi => mdi.MissingDocument && mdi.MissingDocument.name).join(", ")) {
      return missingDocumentInitials.map(mdi => mdi.MissingDocument && mdi.MissingDocument.name).join(", ");
    }
    return null;
  }
}

export default NikoPatientTemplateClinicalEditViewStore;
